import { useEffect, useState } from 'react';
import { LOCATION_WAIT_MS } from '../../utils/fieldOutbox';
import { useGeoWatch } from './useGeoWatch';

/**
 * Camera specimens saved without a place that may still be given one: the
 * outbox waits LOCATION_WAIT_MS from their saving for a fix (offerFix), then
 * asks their collector.
 */
export function waitingForFix(batches, now = Date.now(), waitMs = LOCATION_WAIT_MS) {
  return (batches || []).filter((b) => {
    if (b.status !== 'pending' || b.location || b.source !== 'camera') return false;
    const finished = Date.parse(b.finalizedAt || b.createdAt || '');
    return Number.isFinite(finished) && now - finished < waitMs;
  });
}

/**
 * The camera's offer of a fix, for a screen the person may go to from it.
 *
 * Someone who saves a specimen before the phone found itself and then opens
 * the map would otherwise forfeit the two minutes the camera gives it: the
 * camera is not running to offer a fix. This follows the GPS only while such
 * a specimen is waiting, and hands each fix to the outbox. It shows nothing,
 * and finishes nothing: an unfinished specimen stays the camera's.
 */
export default function FieldFixOffer({ store, outbox, userId }) {
  const [waiting, setWaiting] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const recount = async () => {
      const list = waitingForFix(await store.list(userId));
      if (!alive) return;
      setWaiting(list.length);
      // The wait ends by itself, with nothing in the store changing: looked
      // at again when the first one's does.
      clearTimeout(timer);
      timer = null;
      if (list.length) {
        const ends = Math.min(...list.map(b => Date.parse(b.finalizedAt || b.createdAt))) + LOCATION_WAIT_MS;
        timer = setTimeout(() => { recount().catch(() => {}); }, Math.max(0, ends - Date.now()) + 50);
      }
    };
    recount().catch(() => {});
    const off = store.subscribe(() => { recount().catch(() => {}); });
    return () => {
      alive = false;
      clearTimeout(timer);
      off();
    };
  }, [store, userId]);

  return waiting > 0 ? <OfferFix outbox={outbox} /> : null;
}

/** Mounted only while a specimen waits, so the GPS is not asked otherwise. */
function OfferFix({ outbox }) {
  const { fix } = useGeoWatch();
  useEffect(() => {
    if (fix) outbox.offerFix(fix);
  }, [fix, outbox]);
  return null;
}
